<script lang="tsx">
  import type { PropType } from 'vue';
  import { Result, Button } from 'ant-design-vue';
  import { defineComponent, ref, computed, unref } from 'vue';
  import { ExceptionEnum } from '/@/enums/exceptionEnum';
  import notDataSvg from '/@/assets/svg/no-data.svg';
  import netWorkSvg from '/@/assets/svg/net-error.svg';
  import { useRoute } from 'vue-router';
  import { useDesign } from '/@/hooks/web/useDesign';
  import { useI18n } from '@mcex/locale';
  import { useGo, useRedo } from '/@/hooks/web/usePage';
  import { PageEnum } from '/@/enums/pageEnum';
  import { usePermission } from '/@/hooks/web/usePermission';
  import { RoleEnum } from '/@/enums/roleEnum';
  import { ContainerCard } from '/@/components/Business/Card';
  import { useUserStore } from '/@/store/modules/user';
  import { SvgIcon } from '/@/components/Icon';
  import { LoginStateEnum } from '../login/useLogin';
  import Home from '/@/views/registerUpdate/home.vue';
  import { useBecomeMember } from '/@/hooks/component/useBecomeMember';

  interface MapValue {
    title: string;
    subTitle: string;
    btnText?: string;
    icon?: string;
    handler?: Fn;
    status?: string;
  }

  export default defineComponent({
    name: 'ErrorPage',
    props: {
      // 状态码
      status: {
        type: Number as PropType<number>,
        default: ExceptionEnum.PAGE_NOT_FOUND,
      },

      title: {
        type: String as PropType<string>,
        default: '',
      },

      subTitle: {
        type: String as PropType<string>,
        default: '',
      },

      full: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
    },
    setup(props) {
      const statusMapRef = ref(new Map<string | number, MapValue>());

      const { query } = useRoute();
      const go = useGo();
      const redo = useRedo();
      const { t } = useI18n();
      const { prefixCls } = useDesign('app-exception-page');

      const getStatus = computed(() => {
        const { status: routeStatus } = query;
        const { status } = props;
        return Number(routeStatus) || status;
      });

      const getMapValue = computed((): MapValue => {
        return unref(statusMapRef).get(unref(getStatus)) as MapValue;
      });

      const backLoginI18n = t('sys.exception.backLogin');
      const backHomeI18n = t('sys.exception.backHome');
      const becomMemberI18n = t('layout.header.becomeMember');
      const registerI18n = t('sys.exception.register');

      const { hasRole } = usePermission();
      const userStore = useUserStore();
      const { handleBecomeMember } = useBecomeMember();

      // 用户是否登录
      const isLogin = computed(() => {
        return !!userStore.getToken;
      });

      // 普通会员时引导成为会员按钮
      const btnText403 = computed(() => {
        if (!isLogin.value) {
          return registerI18n;
        }
        if (props.full) {
          return backLoginI18n;
        }
        const nomarl = hasRole([RoleEnum.ORDINARY]);
        if (nomarl) {
          return becomMemberI18n;
        }
        return backHomeI18n;
      });
      const hanlder403 = () => {
        if (!isLogin.value) {
          go(PageEnum.BASE_LOGIN + `?loginState=${LoginStateEnum.REGISTER}`);
          return;
        }
        if (props.full) {
          go(PageEnum.BASE_LOGIN);
          return;
        }
        const nomarl = hasRole([RoleEnum.ORDINARY]);
        if (nomarl) {
          handleBecomeMember();
          return;
        }
        go();
      };

      unref(statusMapRef).set(ExceptionEnum.PAGE_NOT_ACCESS, {
        title: '403',
        status: `${ExceptionEnum.PAGE_NOT_ACCESS}`,
        subTitle: t('sys.exception.subTitle403'),
        btnText: btnText403.value,
        handler: () => hanlder403(),
      });

      unref(statusMapRef).set(ExceptionEnum.PAGE_NOT_FOUND, {
        title: '404',
        status: `${ExceptionEnum.PAGE_NOT_FOUND}`,
        subTitle: t('sys.exception.subTitle404'),
        btnText: props.full ? backLoginI18n : backHomeI18n,
        handler: () => (props.full ? go(PageEnum.BASE_LOGIN) : go()),
      });

      unref(statusMapRef).set(ExceptionEnum.ERROR, {
        title: '500',
        status: `${ExceptionEnum.ERROR}`,
        subTitle: t('sys.exception.subTitle500'),
        btnText: backHomeI18n,
        handler: () => go(),
      });

      unref(statusMapRef).set(ExceptionEnum.PAGE_NOT_DATA, {
        title: t('sys.exception.noDataTitle'),
        subTitle: '',
        btnText: t('common.redo'),
        handler: () => redo(),
        icon: notDataSvg,
      });

      unref(statusMapRef).set(ExceptionEnum.NET_WORK_ERROR, {
        title: t('sys.exception.networkErrorTitle'),
        subTitle: t('sys.exception.networkErrorSubTitle'),
        btnText: t('common.redo'),
        handler: () => redo(),
        icon: netWorkSvg,
      });
      return () => {
        console.log('🚀 ~ file: Exception.vue:163 ~ return ~ isLogin:', isLogin.value);
        const { title, subTitle, btnText, icon, handler, status } = unref(getMapValue) || {};
        return (
          <ContainerCard class={'mt-3'}>
            <div class={prefixCls}>
              {!isLogin.value && (
                <Home sub-title={props.subTitle || subTitle}>
                  {{
                    header: () => <SvgIcon name="exchangeservice-register_frame" size="233" />,
                    extra: () =>
                      btnText && (
                        <Button type="primary" onClick={handler}>
                          {() => btnText}
                        </Button>
                      ),
                    icon: () => <SvgIcon name="no_permission" size="160" />,
                  }}
                </Home>
              )}
              {isLogin.value && (
                <Result
                  status={status as any}
                  title={props.title || title}
                  sub-title={props.subTitle || subTitle}
                >
                  {{
                    extra: () =>
                      btnText && (
                        <Button type="primary" onClick={handler}>
                          {() => btnText}
                        </Button>
                      ),
                    icon: () => (icon ? <img src={icon} /> : null),
                  }}
                </Result>
              )}
            </div>
          </ContainerCard>
        );
      };
    },
  });
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-app-exception-page';

  .@{prefix-cls} {
    @height: calc(100vh - @right-top - (@first-card-spacing*2));
    height: @height;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    .ant-result-icon {
      img {
        max-width: 400px;
        max-height: 300px;
      }
    }
  }
</style>
